import React, { useState, useEffect } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface Invitation {
  id: string;
  chamaName: string;
  chamaDescription?: string;
  inviterName: string;
  contributionAmount: number;
  contributionFrequency: string;
  createdAt: string;
  status: 'pending' | 'accepted' | 'rejected';
}

const Invitations: React.FC = () => {
  const colors = getThemeColors('dark');

  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);

  // Mock invitations data - replace with actual API call
  const mockInvitations: Invitation[] = [
    {
      id: '1',
      chamaName: 'Savings Group',
      chamaDescription: 'A group focused on saving for future investments',
      inviterName: 'John Doe',
      contributionAmount: 5000,
      contributionFrequency: 'monthly',
      createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
      status: 'pending'
    },
    {
      id: '2',
      chamaName: 'Investment Club',
      chamaDescription: 'Building wealth through smart investments',
      inviterName: 'Jane Smith',
      contributionAmount: 10000,
      contributionFrequency: 'weekly',
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
      status: 'pending'
    },
    {
      id: '3',
      chamaName: 'Emergency Fund',
      chamaDescription: 'Preparing for unexpected expenses',
      inviterName: 'Mike Johnson',
      contributionAmount: 3000,
      contributionFrequency: 'monthly',
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
      status: 'accepted'
    }
  ];

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setInvitations(mockInvitations);
    } catch (error) {
      console.error('Failed to load invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const respondToInvitation = async (invitationId: string, action: 'accept' | 'reject') => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update local state
      setInvitations(prev =>
        prev.map(invitation =>
          invitation.id === invitationId
            ? { ...invitation, status: action === 'accept' ? 'accepted' : 'rejected' }
            : invitation
        )
      );

      alert(action === 'accept'
        ? 'You have successfully joined the chama!'
        : 'Invitation rejected successfully'
      );
    } catch (error) {
      alert('Failed to respond to invitation');
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - time.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return time.toLocaleDateString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const pendingInvitations = invitations.filter(inv => inv.status === 'pending');
  const respondedInvitations = invitations.filter(inv => inv.status !== 'pending');

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80,
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80,
      paddingBottom: 80,
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: spacing.md,
    },
    section: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      padding: spacing.lg,
      marginBottom: spacing.lg,
      ...getShadowStyle('sm'),
    },
    sectionTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.md,
    },
    invitationCard: {
      backgroundColor: colors.backgroundSecondary,
      borderRadius: borderRadius.md,
      padding: spacing.md,
      marginBottom: spacing.md,
      border: `1px solid ${colors.border}`,
    },
    invitationHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: spacing.sm,
    },
    chamaName: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.xs,
    },
    inviterText: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginBottom: spacing.xs,
    },
    contributionText: {
      fontSize: typography.fontSize.base,
      color: colors.primary,
      fontWeight: typography.fontWeight.semibold,
      marginBottom: spacing.sm,
    },
    descriptionText: {
      fontSize: typography.fontSize.sm,
      color: colors.text,
      lineHeight: 1.5,
      marginBottom: spacing.sm,
    },
    timeText: {
      fontSize: typography.fontSize.xs,
      color: colors.textTertiary,
    },
    actionButtons: {
      display: 'flex',
      gap: spacing.sm,
      marginTop: spacing.sm,
    },
    acceptButton: {
      backgroundColor: colors.success,
      color: colors.white,
      border: 'none',
      padding: `${spacing.sm} ${spacing.md}`,
      borderRadius: borderRadius.md,
      cursor: 'pointer',
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.semibold,
    },
    rejectButton: {
      backgroundColor: colors.error,
      color: colors.white,
      border: 'none',
      padding: `${spacing.sm} ${spacing.md}`,
      borderRadius: borderRadius.md,
      cursor: 'pointer',
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.semibold,
    },
    statusBadge: {
      padding: `${spacing.xs} ${spacing.sm}`,
      borderRadius: borderRadius.sm,
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.semibold,
      textTransform: 'uppercase',
    },
    statusAccepted: {
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
      color: '#4CAF50',
    },
    statusRejected: {
      backgroundColor: 'rgba(244, 67, 54, 0.1)',
      color: '#f44336',
    },
    emptyState: {
      textAlign: 'center',
      padding: spacing.xxxl,
      color: colors.textSecondary,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing.xxxl,
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div>Loading invitations...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Chama Invitations"
        subtitle="Manage your pending chama invitations"
        showBackButton={true}
        showHomeButton={false}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          {/* Pending Invitations */}
          {pendingInvitations.length > 0 && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Pending Invitations ({pendingInvitations.length})</h2>
              {pendingInvitations.map((invitation) => (
                <div key={invitation.id} style={styles.invitationCard}>
                  <div style={styles.invitationHeader}>
                    <div>
                      <h3 style={styles.chamaName}>{invitation.chamaName}</h3>
                      <p style={styles.inviterText}>Invited by {invitation.inviterName}</p>
                    </div>
                    <span style={styles.timeText}>{getTimeAgo(invitation.createdAt)}</span>
                  </div>

                  <p style={styles.contributionText}>
                    Contribution: {formatCurrency(invitation.contributionAmount)} {invitation.contributionFrequency}
                  </p>

                  {invitation.chamaDescription && (
                    <p style={styles.descriptionText}>{invitation.chamaDescription}</p>
                  )}

                  <div style={styles.actionButtons}>
                    <button
                      style={styles.rejectButton}
                      onClick={() => respondToInvitation(invitation.id, 'reject')}
                    >
                      Reject
                    </button>
                    <button
                      style={styles.acceptButton}
                      onClick={() => respondToInvitation(invitation.id, 'accept')}
                    >
                      Accept
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Responded Invitations */}
          {respondedInvitations.length > 0 && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Previous Invitations</h2>
              {respondedInvitations.map((invitation) => (
                <div key={invitation.id} style={styles.invitationCard}>
                  <div style={styles.invitationHeader}>
                    <div>
                      <h3 style={styles.chamaName}>{invitation.chamaName}</h3>
                      <p style={styles.inviterText}>Invited by {invitation.inviterName}</p>
                    </div>
                    <div style={{
                      ...styles.statusBadge,
                      ...(invitation.status === 'accepted' ? styles.statusAccepted : styles.statusRejected)
                    }}>
                      {invitation.status}
                    </div>
                  </div>

                  <p style={styles.contributionText}>
                    Contribution: {formatCurrency(invitation.contributionAmount)} {invitation.contributionFrequency}
                  </p>

                  {invitation.chamaDescription && (
                    <p style={styles.descriptionText}>{invitation.chamaDescription}</p>
                  )}

                  <span style={styles.timeText}>{getTimeAgo(invitation.createdAt)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {invitations.length === 0 && (
            <div style={styles.emptyState}>
              <h2>No invitations yet</h2>
              <p>You'll see chama invitations here when they arrive</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Invitations;